import React, { Fragment } from "react";

import Label from "@busbud/design-system-components/dist/Label";
import Numerical from "@busbud/design-system-components/dist/Numerical";
import OriginDestinationLabel from "@busbud/design-system-components/dist/OriginDestinationLabel";
import Stack from "@busbud/design-system-components/dist/Stack";

import { LocalizedPrice } from "@app/components/localized-price";
import { useAppContext } from "@app/helpers/hooks";
import { getTranslatedPassengerCategory } from "@app/helpers/translations";
import type { CurrencyCode } from "@app/types/currency";

export type DepartureSummaryPassenger = {
  price_in_cents: number;
  category?: string;
};
export interface DepartureSummaryProps {
  currency: CurrencyCode;
  passenger_first_names: string[];
  origin_city?: string | null;
  destination_city?: string | null;
  charges_per_passenger: DepartureSummaryPassenger[];
}

const DepartureSummary: React.FC<DepartureSummaryProps> = ({
  currency,
  passenger_first_names,
  origin_city,
  destination_city,
  charges_per_passenger
}) => {
  const { translator, features } = useAppContext();

  if (!charges_per_passenger.length) {
    return null;
  }

  const renderPassengerBreakdown = () => {
    return charges_per_passenger.map((passenger, index) => {
      return (
        <Stack direction="row" justifyContent="space-between" key={index}>
          <Label size="md" fontWeight="bold">
            {getPassengerName(index)}
          </Label>
          <Numerical size="md">
            <LocalizedPrice
              price_in_cents={passenger.price_in_cents}
              currency={currency}
            />
          </Numerical>
        </Stack>
      );
    });
  };

  const getPassengerName = (passenger_index: number) => {
    const passenger_name = passenger_first_names[passenger_index];
    if (passenger_name?.length) {
      return passenger_name;
    }
    if (features.ADULT_ONLY_SEARCH) {
      return translator.t("!checkout.summary.traveler-no-category", {
        number: passenger_index + 1
      });
    }

    // @ts-expect-error TS2339 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const { category } = charges_per_passenger[passenger_index];

    return translator.t("!checkout.summary.traveler", {
      number: passenger_index + 1,
      category: getTranslatedPassengerCategory(category || "", translator)
    });
  };

  return (
    <Fragment>
      <Stack direction="column" spacing={1.5}>
        {!!origin_city && !!destination_city && (
          <OriginDestinationLabel
            origin={origin_city}
            destination={destination_city}
            size="md"
          />
        )}
        <Stack direction="column" spacing={1.5}>
          {renderPassengerBreakdown()}
        </Stack>
      </Stack>
    </Fragment>
  );
};

export { DepartureSummary };
